import React from 'react';
import type { ReactNode } from 'react';
import { RichTextMenu } from '@puckeditor/core';
import type { Field } from '@puckeditor/core';
import styled from 'styled-components';

import { findFragmentedTokens } from '../../interpolate-html';
import { CMS_VARIABLE_KEYS } from '../../interpolation';

type RichtextField = Extract<Field, { type: 'richtext' }>;
type RichtextMenuRenderer = NonNullable<RichtextField['renderMenu']>;
export type CmsRichTextMenuProps = Parameters<RichtextMenuRenderer>[0];
type RichtextSelector = NonNullable<NonNullable<RichtextField['tiptap']>['selector']>;

const TOKEN_STATE_PREFIX = 'cmsFragmentedToken_';

function tokenStateKey(key: string): string {
  return `${TOKEN_STATE_PREFIX}${key}`;
}

/**
 * Puck's selector return values are boolean-only. One stable key per supported CMS variable makes
 * formatting transactions re-render the warning without returning raw editor HTML through state.
 */
export const cmsRichTextSelector: RichtextSelector = ({ editor }) => {
  const fragmented = new Set(editor && !editor.isDestroyed ? findFragmentedTokens(editor.getHTML()) : []);
  return Object.fromEntries(CMS_VARIABLE_KEYS.map((key) => [tokenStateKey(key), fragmented.has(key)]));
};

const MenuRows = styled.div`
  display: grid;
  gap: var(--dcm-cms-richtext-menu-row-gap, 6px);
  min-width: 0;
  width: 100%;
`;

const NoticeStack = styled.div`
  display: grid;
  gap: var(--dcm-cms-richtext-notice-gap, 4px);
  min-width: 0;
`;

const LinkHint = styled.p`
  background: var(--dcm-cms-richtext-hint-background, #fff);
  border: 1px solid var(--dcm-cms-richtext-hint-border, #d0d4d8);
  border-radius: var(--dcm-cms-richtext-hint-radius, 4px);
  color: var(--dcm-cms-richtext-hint-color, #646d72);
  font-size: var(--dcm-cms-richtext-hint-size, 12px);
  margin: 0;
  overflow-wrap: anywhere;
  padding: var(--dcm-cms-richtext-hint-padding, 4px 6px);
`;

const TokenWarning = styled.div`
  background: var(--dcm-cms-richtext-warning-background, #fff4d6);
  border: 1px solid var(--dcm-cms-richtext-warning-border, #b77900);
  border-radius: var(--dcm-cms-richtext-warning-radius, 4px);
  color: var(--dcm-cms-richtext-warning-color, #6d4300);
  font-size: var(--dcm-cms-richtext-warning-size, 12px);
  overflow-wrap: anywhere;
  padding: var(--dcm-cms-richtext-warning-padding, 6px 8px);
`;

function fragmentedKeys(editorState: CmsRichTextMenuProps['editorState']): string[] {
  return CMS_VARIABLE_KEYS.filter((key) => editorState?.[tokenStateKey(key)] === true);
}

function warningText(keys: string[]): string {
  const labels = keys.map((key) => `{{${key}}}`);
  const subject = labels.length === 1 ? `Token ${labels[0]} is` : `Tokens ${labels.join(', ')} are`;
  return `${subject} split by formatting or a line break. Rejoin the token before publishing.`;
}

type NoticesProps = {
  editorState: CmsRichTextMenuProps['editorState'];
};

function Notices({ editorState }: NoticesProps): JSX.Element {
  const fragmented = fragmentedKeys(editorState);
  return (
    <NoticeStack data-testid='cms-richtext-menu-notices'>
      <LinkHint data-testid='cms-richtext-link-hint'>To add a link, select text and paste a URL.</LinkHint>
      {fragmented.length > 0 ? (
        <TokenWarning role='status' aria-live='polite'>
          {warningText(fragmented)}
        </TokenWarning>
      ) : null}
    </NoticeStack>
  );
}

type MenuFrameProps = {
  controls: ReactNode;
  editorState: CmsRichTextMenuProps['editorState'];
};

function MenuFrame({ controls, editorState }: MenuFrameProps): JSX.Element {
  return (
    <MenuRows data-testid='cms-richtext-menu-rows'>
      {controls}
      <Notices editorState={editorState} />
    </MenuRows>
  );
}

/** Puck portals this into the canvas action bar, where the iframe cannot guarantee notice space. */
export function CmsRichTextMenu({ children }: CmsRichTextMenuProps): JSX.Element {
  return <RichTextMenu>{children}</RichTextMenu>;
}

/**
 * Text's full menu: every native default except Heading. Heading semantics belong to the distinct
 * Heading component; native List/emphasis/Align and native paste-over-selection links remain.
 */
export function CmsTextRichTextMenu({ editorState }: CmsRichTextMenuProps): JSX.Element {
  const controls = (
    <RichTextMenu>
      <RichTextMenu.Group>
        <RichTextMenu.ListSelect />
      </RichTextMenu.Group>
      <RichTextMenu.Group>
        <RichTextMenu.Bold />
        <RichTextMenu.Italic />
        <RichTextMenu.Underline />
      </RichTextMenu.Group>
      <RichTextMenu.Group>
        <RichTextMenu.AlignSelect />
      </RichTextMenu.Group>
    </RichTextMenu>
  );
  return <MenuFrame controls={controls} editorState={editorState} />;
}
